import React, { useState, useEffect, useCallback, useMemo } from "react";
import { s3UtilityService } from "../../services/S3Utilities";
import { S3StagingManager } from "../../services/S3_Staging_Manager";
import { S3UtilityController } from "../../services/S3_Utility_Controller";
import { useNavigate } from "react-router-dom";
import { useNavigationContext } from "../../../common/navigation/NavigationContext";

/**
 * 🛰️ S3 MONITOR VIEW
 * Hosted Version: Logic preserved, page-level HTML formatting removed.
 * Linting: Navigation hooks preserved but silenced for Hub hosting.
 */
export default function S3MonitorView({ targetBucket, config, onFileSelect }) {
    // 🏗️ STATE HOOKS (Preserved)
    const [inventory, setInventory] = useState([]);
    const [selectedPaths, setSelectedPaths] = useState([]);
    const [isSyncing, setIsSyncing] = useState(false);
    const [filter, setFilter] = useState("");
    const [stagedChanges, setStagedChanges] = useState({}); 
    const [workerStatus, setWorkerStatus] = useState({ active: false, current: 0, total: 0, label: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // 🎯 LINT FIX: Preserving hooks for standalone use, silencing for Hub hosting
    // eslint-disable-next-line no-unused-vars
    const navigate = useNavigate();
    // eslint-disable-next-line no-unused-vars
    const { activeWorkflow } = useNavigationContext();

    // 📋 REFRESH: Sync with AWS (Preserved)
    const refreshS3Inventory = useCallback(async () => {
        if (!targetBucket || !config?.siteKey) return;
        setIsSyncing(true);
        try {
            const files = await s3UtilityService.getInventory(targetBucket, config, "");
            setInventory(files || []);
            setStagedChanges({}); 
            setCurrentPage(1);
        } catch (err) {
            console.error("❌ Inventory Fetch Failed:", err);
        } finally {
            setIsSyncing(false);
        }
    }, [targetBucket, config]);

    useEffect(() => { refreshS3Inventory(); }, [refreshS3Inventory]);

    // 🔍 FILTERING & PAGINATION (Preserved)
    const filteredInventory = useMemo(() => {
        const search = filter.toLowerCase();
        return inventory.filter(f => f.filename.toLowerCase().includes(search));
    }, [inventory, filter]);

    const totalPages = Math.ceil(filteredInventory.length / itemsPerPage);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredInventory.slice(start, start + itemsPerPage);
    }, [filteredInventory, currentPage, itemsPerPage]);

    // 🏷️ LABEL INTERACTION (Preserved)
    const handleTagEdit = (item, existingKey = null) => {
        const input = window.prompt(
            `LABEL AUTHORITY [${item.filename}]:\n(Clear the text and click OK to delete this label)`, 
            existingKey || ""
        );
        const nextStaged = S3StagingManager.stageUpdate(item, stagedChanges, input, existingKey);
        setStagedChanges(nextStaged);
    };

    // 💾 SAVE (Preserved)
    const handleSave = async () => {
        setIsSyncing(true);
        try {
            await S3StagingManager.commitChanges(targetBucket, config, stagedChanges, (curr, tot, path) => {
                setWorkerStatus({ active: true, current: curr, total: tot, label: `Saving: ${path}` });
            });
            await refreshS3Inventory();
        } finally {
            setWorkerStatus({ active: false, current: 0, total: 0, label: "" });
            setIsSyncing(false);
        }
    };

    // 🛠️ UTILITY DISPATCH (Preserved)
    const handleUtilityClick = (action) => {
        S3UtilityController.execute(action, selectedPaths, {
            targetBucket,
            config,
            setWorkerStatus,
            refreshS3Inventory,
            setSelectedPaths
        });
    };

    const formatBytes = (bytes) => {
        if (!+bytes) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    return (
        <div className="s3-monitor-embedded position-relative">
            
            {/* 🚀 SPINNING HALO */}
            {(isSyncing || workerStatus.active) && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 1050 }}>
                    <div className="spinner-grow text-dark mb-3"></div>
                    <div className="font-monospace extra-small fw-bold text-dark text-uppercase bg-white p-2 border shadow-sm">
                        {isSyncing ? "Syncing AWS Inventory..." : workerStatus.label}
                    </div>
                </div>
            )}

            {/* 1. TOOLBAR */}
            <div className="d-flex justify-content-between align-items-end p-3 border-bottom bg-light">
                <div>
                    <h6 className="extra-small fw-bold mb-1 text-muted text-uppercase">Bucket Authority</h6>
                    <span className="badge bg-dark extra-small font-monospace">{targetBucket}</span>
                </div>
                
                <div className="d-flex align-items-center">
                    {Object.keys(stagedChanges).length > 0 && (
                        <div className="me-3">
                            <button className="btn btn-sm btn-success fw-bold extra-small me-1 shadow-sm" onClick={handleSave}>SAVE CHANGES</button>
                            <button className="btn btn-sm btn-outline-secondary fw-bold extra-small shadow-sm" onClick={() => setStagedChanges({})}>DISCARD</button>
                        </div>
                    )}
                    {selectedPaths.length > 0 && (
                        <div className="me-2">
                            <button className="btn btn-sm btn-outline-danger mx-1 extra-small fw-bold shadow-sm" onClick={() => handleUtilityClick("Remove")}>DELETE</button>
                            <button className="btn btn-sm btn-dark mx-1 extra-small fw-bold shadow-sm" onClick={() => handleUtilityClick("BulkClean")}>CLEAN</button>
                            <button className="btn btn-sm btn-dark mx-1 extra-small fw-bold shadow-sm" onClick={() => handleUtilityClick("Rename")}>RENAME</button>
                            <button className="btn btn-sm btn-primary mx-1 extra-small fw-bold shadow-sm" onClick={() => onFileSelect && onFileSelect(inventory.find(f => f.path === selectedPaths[0]))}>SELECT</button>
                        </div>
                    )}
                    <button className="btn btn-sm btn-outline-dark fw-bold extra-small px-3 shadow-sm" onClick={refreshS3Inventory}>REFRESH</button>
                </div>
            </div>

            {/* 2. SETTINGS */}
            <div className="d-flex gap-2 p-2 border-bottom">
                <div className="input-group input-group-sm">
                    <span className="input-group-text bg-transparent border-0 text-muted"><i className="bi bi-search"></i></span>
                    <input type="text" className="form-control border-0 shadow-none" placeholder="Filter inventory..." onChange={(e) => { setFilter(e.target.value); setCurrentPage(1); }} />
                </div>
                <div className="d-flex align-items-center border-start ps-2">
                    <span className="extra-small text-muted me-2 text-uppercase fw-bold">Rows:</span>
                    <select 
                        className="form-select form-select-sm border-0 shadow-none extra-small fw-bold"
                        value={itemsPerPage}
                        onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                </div>
            </div>

            {/* 3. TABLE */}
            <div className="table-responsive">
                <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                        <tr className="extra-small text-muted">
                            <th style={{ width: "45px" }} className="ps-3"></th>
                            <th className="py-3 text-uppercase">Object Key</th>
                            <th className="py-3 text-uppercase">Size</th>
                            <th className="text-end pe-3 py-3 text-uppercase">Labels</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((item, idx) => {
                            const isStaged = !!stagedChanges[item.path];
                            const activeTags = isStaged ? stagedChanges[item.path].tags : item.tags;

                            return (
                                <tr key={idx} className={isStaged ? "table-warning bg-opacity-10" : ""}>
                                    <td className="ps-3">
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input shadow-none" 
                                            checked={selectedPaths.includes(item.path)} 
                                            onChange={() => setSelectedPaths(prev => prev.includes(item.path) ? prev.filter(p => p !== item.path) : [...prev, item.path])} 
                                        />
                                    </td>
                                    <td className="py-2">
                                        <div className="fw-bold small text-dark text-truncate" style={{maxWidth: "350px"}}>{item.filename}</div>
                                        {isStaged && <span className="extra-small text-warning fw-bold text-uppercase" style={{fontSize: "8px"}}>● Pending Save</span>}
                                    </td>
                                    <td><span className="extra-small text-muted">{formatBytes(item.size)}</span></td>
                                    <td className="text-end pe-3">
                                        <div className="d-flex flex-wrap justify-content-end align-items-center gap-1">
                                            {Object.entries(activeTags || {}).map(([k, v]) => (
                                                <span 
                                                    key={k} 
                                                    className={`badge border extra-small p-1 ${isStaged ? 'bg-warning-subtle text-dark border-warning' : 'bg-light text-secondary'}`}
                                                    style={{ fontSize: "9px", cursor: "pointer" }} 
                                                    onClick={() => handleTagEdit(item, k)}
                                                >
                                                    {k}
                                                </span>
                                            ))}
                                            <button className="btn btn-xs btn-outline-secondary p-0 px-1 border-dotted" style={{ fontSize: "10px", lineHeight: "1" }} onClick={() => handleTagEdit(item)}>+</button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* 🔢 PAGINATION */}
            {!isSyncing && totalPages > 1 && (
                <div className="card-footer bg-white border-top-0 d-flex justify-content-center py-2">
                    <ul className="pagination pagination-sm mb-0">
                        {[...Array(totalPages)].map((_, i) => (
                            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                <button className={`page-link border-0 shadow-none ${currentPage === i + 1 ? 'bg-dark text-white' : 'text-dark'}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}